import queryString from 'query-string'
import { useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useLocation } from 'react-router-dom'

import { useAuth } from 'components/AuthProvider'
import { loginPath, resendConfirmationEmailPath } from 'components/Routes'

import {
  VisibilityOffRounded as VisibilityOffIcon,
  VisibilityRounded as VisibilityIcon
} from '@mui/icons-material'
import {
  Grid2,
  IconButton,
  InputAdornment,
  Link,
  Typography
} from '@mui/material'
import Button from 'components/Button'
import DefaultLayout from 'components/layout/DefaultLayout'
import { Link as RouterLink } from 'react-router-dom'

import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import PasswordProgress from 'components/PasswordProgress'
import { Field, FieldProps, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(2)
  },
  link: {
    color: theme.palette.primary.main
  }
}))

interface FormValues {
  email: string
  confirmationCode: string
  newPassword: string
  confirmPassword: string
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Email is invalid').required('Email is required'),
  confirmationCode: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Enter the 6-digit code from your email'),
  confirmPassword: Yup.string()
    .required('Confirm new password is required')
    .oneOf([Yup.ref('newPassword'), null], `Confirm new password doesn't match`)
})

const requestValidationSchema = Yup.object().shape({
  email: Yup.string().email('Email is invalid').required('Email is required')
})

// BUG-011: manual arrivals ask ONLY for an email address (step 1); the emailed
// link lands straight on the password form (step 2). The four-field
// email + code + password form is kept only as a manual-entry fallback.
type Phase = 'request' | 'sent' | 'form'

const headings: Record<Phase, string> = {
  request: 'Reset password',
  sent: 'Check your email',
  form: 'Change password'
}

export default function ResetPassword(): JSX.Element {
  const { classes } = useStyles()
  const location = useLocation()

  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [disableButton, setDisableButton] = useState<boolean>(false)
  // Fallback for anyone whose emailed link won't open: the check-your-email
  // state can reveal the email + 6-digit code + password form.
  const [showCodeForm, setShowCodeForm] = useState<boolean>(false)

  const handleToggleShowPassword = () => setShowPassword(!showPassword)

  const { requestReset, reset, loading } = useAuth()

  // The reset email (the same SendGrid template Rails sent) renders a link to
  // /reset_password?reset_token=<code>&email=<email>. Read the code from the
  // query so clicking the link lands here ready to go; typing the code by hand
  // still works, and both are the same 6-digit value.
  //
  // `reset_token` is Rails' parameter name, kept so any older email still in an
  // inbox continues to work. `code` is accepted as an alias.
  const {
    email: emailFromQuery,
    reset_token: tokenFromQuery,
    code: codeFromQuery
  } = queryString.parse(location.search)

  const initialValues: FormValues = {
    email: (emailFromQuery as string) ?? '',
    confirmationCode: ((tokenFromQuery ?? codeFromQuery) as string) ?? '',
    newPassword: '',
    confirmPassword: ''
  }

  // Arriving via the emailed link means both values are already in the query
  // string, so only ask for the new password (Rails-parity UX). The email and
  // code stay in form state and are submitted exactly as before — they're
  // just not shown.
  const arrivedViaEmailLink = Boolean(
    emailFromQuery && (tokenFromQuery ?? codeFromQuery)
  )

  // In-app two-step flow: a manual arrival with no code in the query asks only
  // for the email. Submitting fires the same forgot-password request the
  // Forgot password page uses; on success AuthProvider navigates back here
  // with ?email=<address>, which renders the check-your-email confirmation
  // state (arrivals from the Forgot password page land there the same way).
  const phase: Phase =
    arrivedViaEmailLink || showCodeForm
      ? 'form'
      : emailFromQuery
      ? 'sent'
      : 'request'

  const recaptchaEnabled = JSON.parse(
    process.env.REACT_APP_RECAPTCHA_ENABLED || 'true'
  )
  const { executeRecaptcha } = useGoogleReCaptcha()

  async function handleSubmit(values: FormValues) {
    if (!executeRecaptcha && recaptchaEnabled) return

    const recaptchaToken =
      recaptchaEnabled &&
      executeRecaptcha &&
      (await executeRecaptcha('passwords/reset'))

    reset(
      values.email,
      values.confirmationCode,
      values.newPassword,
      recaptchaToken || undefined
    )
  }

  async function handleRequestReset(email: string) {
    if (!executeRecaptcha && recaptchaEnabled) return

    const recaptchaToken =
      recaptchaEnabled &&
      executeRecaptcha &&
      (await executeRecaptcha('passwords/send_reset_instructions'))

    requestReset(email, recaptchaToken || undefined)
  }

  const renderHeader = () => {
    return (
      <Typography component='h1' variant='h1' fontSize='4.5rem' color='primary'>
        {headings[phase]}
      </Typography>
    )
  }

  const renderLoginLink = () => (
    <Grid2 container>
      <Link
        color='textPrimary'
        component={RouterLink}
        to={loginPath()}
        underline='always'
        className={classes.link}
      >
        Go back to login
      </Link>
    </Grid2>
  )

  // Step 1: manual arrival — ask only for the email address.
  const renderRequestForm = () => (
    <Formik
      initialValues={{ email: '' }}
      validationSchema={requestValidationSchema}
      onSubmit={values => handleRequestReset(values.email)}
    >
      {({ setFieldValue, handleSubmit }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12 }}>
              <Field name='email'>
                {({ field, form, meta }: FieldProps) => (
                  <TextField
                    field={field}
                    form={form}
                    meta={meta}
                    fullWidth
                    required
                    label='Email address'
                    onChange={e => {
                      setFieldValue('email', e.target.value)
                    }}
                  />
                )}
              </Field>
            </Grid2>

            <Grid2 mt={2} size={{ xs: 12 }}>
              <Button
                color='primary'
                fullWidth
                disabled={loading}
                type='submit'
              >
                Send reset link
              </Button>
            </Grid2>

            {renderLoginLink()}
          </Grid2>
        </form>
      )}
    </Formik>
  )

  // Confirmation state after the reset email has been requested.
  const renderCheckEmail = () => (
    <Grid2 container spacing={2} className={classes.form}>
      <Grid2 size={{ xs: 12 }}>
        <Typography variant='body1'>
          We have sent a password reset link to{' '}
          <strong>{emailFromQuery as string}</strong>. Click the link in the
          email to choose a new password.
        </Typography>
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <Typography variant='body1'>
          If it has not arrived after a couple of minutes, check your spam
          folder.
        </Typography>
      </Grid2>

      <Grid2 mt={2} size={{ xs: 12 }}>
        <Button
          color='primary'
          variant='outlined'
          fullWidth
          disabled={loading}
          onClick={() => handleRequestReset(emailFromQuery as string)}
        >
          Resend email
        </Button>
      </Grid2>

      <Grid2 size={{ xs: 12 }}>
        <Typography variant='body1'>
          <Link
            component='button'
            type='button'
            underline='always'
            className={classes.link}
            onClick={() => setShowCodeForm(true)}
          >
            Enter the 6-digit code manually
          </Link>
        </Typography>
      </Grid2>

      {renderLoginLink()}
    </Grid2>
  )

  // Step 2 (emailed link) and the manual-code fallback share this form; the
  // email + code fields only render for the fallback.
  const renderPasswordForm = () => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, handleSubmit }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Grid2 container spacing={2}>
            {!arrivedViaEmailLink && (
              <>
                <Grid2 size={{ xs: 12 }}>
                  <Field name='email'>
                    {({ field, form, meta }: FieldProps) => (
                      <TextField
                        field={field}
                        form={form}
                        meta={meta}
                        fullWidth
                        required
                        label='Email address'
                        onChange={e => {
                          setFieldValue('email', e.target.value)
                        }}
                      />
                    )}
                  </Field>
                </Grid2>

                <Grid2 size={{ xs: 12 }}>
                  <Field name='confirmationCode'>
                    {({ field, form, meta }: FieldProps) => (
                      <TextField
                        field={field}
                        form={form}
                        meta={meta}
                        fullWidth
                        required
                        label='Verification code'
                        inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                        onChange={e => {
                          setFieldValue('confirmationCode', e.target.value)
                        }}
                      />
                    )}
                  </Field>
                </Grid2>
              </>
            )}

            <Grid2 size={{ xs: 12 }}>
              <PasswordProgress
                autoComplete='new-password'
                label='New password'
                required
                onChange={(password: string, hasError?: boolean) => {
                  setFieldValue('newPassword', password)
                  if (hasError) setDisableButton(true)
                  else setDisableButton(false)
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Field name='confirmPassword' autoComplete='new-password'>
                {({ field, form, meta }: FieldProps) => (
                  <TextField
                    field={field}
                    form={form}
                    meta={meta}
                    disabled={false}
                    fullWidth
                    label='Confirm new password'
                    required
                    onChange={e => {
                      setFieldValue('confirmPassword', e.target.value)
                    }}
                    InputProps={{
                      style: { background: '#ffffff' },
                      // This is where the toggle 'view password' button is added
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => handleToggleShowPassword()}
                            size='large'
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <VisibilityIcon />
                            ) : (
                              <VisibilityOffIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ background: 'none' }}
                    type={showPassword ? 'text' : 'password'}
                  />
                )}
              </Field>
            </Grid2>

            <Grid2 mt={2} size={{ xs: 12 }}>
              <Button
                color='primary'
                fullWidth
                disabled={loading || disableButton}
                type='submit'
              >
                Change password
              </Button>
            </Grid2>
            {renderLoginLink()}
            <Grid2 container>
              <Typography align='center' variant='body1'>
                <Link
                  color='textPrimary'
                  component={RouterLink}
                  to={resendConfirmationEmailPath()}
                  underline='always'
                  className={classes.link}
                >
                  Resend confirmation email
                </Link>
              </Typography>
            </Grid2>
          </Grid2>
        </form>
      )}
    </Formik>
  )

  return (
    <DefaultLayout
      backgroundColor='white'
      containerMaxWidth='xs'
      heading={renderHeader()}
    >
      {phase === 'request' && renderRequestForm()}
      {phase === 'sent' && renderCheckEmail()}
      {phase === 'form' && renderPasswordForm()}
    </DefaultLayout>
  )
}
